/**
 * Contratos do Clube pratiGO no app do cliente (PRAT-27).
 * Endpoints em `api/app/merchants/{slug}/loyalty…` — ver
 * `pratigo-api-laravel/docs/features/clube_pratigo/api-app.md`.
 */
export const CLUB_ENDPOINT = (slug: string) => `api/app/merchants/${slug}/loyalty`;

export type ClubTierKey = 'bronze' | 'silver' | 'gold' | 'platinum';

export interface ClubProgram {
  points_per_real: number;
  product_multiplier: number;
  delivery_earns_points: boolean;
  discount_applies_to_delivery: boolean;
  expiration: { type: 'never' | 'months'; months: number | null };
  channels: Record<string, boolean>;
}

export interface ClubTier {
  key: ClubTierKey;
  name: string;
  min_points: number;
  max_points: number | null;
  color: string;
  benefits: string[];
}

export interface ClubCustomer {
  enrolled: boolean;
  available_points: number;
  total_points: number;
  used_points: number;
  tier_points: number;
  tier: { key: ClubTierKey; name: string; color: string };
  next_tier: { key: ClubTierKey; name: string; min_points: number } | null;
  points_to_next_tier: number;
  progress_percent: number;
  expiring: { points: number; at: string } | null;
  joined_at: string | null;
}

export interface ClubSummary {
  enabled: boolean;
  program: ClubProgram | null;
  tiers: ClubTier[];
  /** `null` quando a requisição foi feita sem token. */
  customer: ClubCustomer | null;
}

export interface ClubCatalogItem {
  id: string;
  name: string;
  description: string | null;
  image: string | null;
  category: string | null;
  price: number;
  effective_price: number;
  /** PRAT-153: preço "a partir de" (base + complementos obrigatórios mais baratos). */
  min_price?: number;
  /** PRAT-153: o preço final depende dos complementos escolhidos. */
  has_price_from?: boolean;
  points_required: number;
  /** `null` sem token. */
  is_affordable: boolean | null;
  /** Combo ou produto com complemento obrigatório: precisa da tela do produto. */
  requires_customization: boolean;
}

export interface ClubCatalog {
  enabled: boolean;
  product_multiplier: number | null;
  available_points: number | null;
  items: ClubCatalogItem[];
}

export type ClubEntryType = 'earned' | 'redeemed' | 'expired' | 'adjustment';

export interface ClubStatementEntry {
  id: string;
  type: ClubEntryType;
  points: number;
  description: string | null;
  order: { id: string; display_id: string | number | null; total: number } | null;
  order_value: number | null;
  channel: string | null;
  created_at: string | null;
  expires_at: string | null;
  expired_at: string | null;
  balance_after: number;
}

export interface ClubStatement {
  customer: ClubCustomer | null;
  totals: { earned: number; used: number; expired: number };
  entries: ClubStatementEntry[];
}

export interface ClubSlugRequest {
  slug: string;
}
